/**
 * HaulbackRecordModal
 *
 * Read-only detail modal for a single Haulback submission record.
 * Opens when a row is tapped in the One Way Haulbacks table.
 *
 * Features
 * ─────────
 * • Displays all record fields read-only
 * • Photos section when the reason code has pictureRequired
 * • Full-screen photo viewer with prev/next/close (swipe on mobile)
 * • "Submitted Note" section when the reason code has textFieldRequired
 * • "Notify my Supervisor" button → NotifySupervisorModal
 * • Close via X button, "Close" footer button, or Escape key (web)
 * • Shows toast and refreshes table when the record no longer exists
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Platform,
  useWindowDimensions,
  StyleSheet,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import { FontFamily } from '../../constants/fonts';
import {
  fetchHaulbackSubmissionById,
  RecentHaulbackSubmission,
} from '../../api/deliveryRpmApi';
import PTLanguageToggle from '../PerfectTruck/PTLanguageToggle';
import NotifySupervisorModal from './NotifySupervisorModal';
import TruckPhotoGrid from './components/TruckPhotoGrid';
import { useDarkStyles, useDarkColor } from '../../theme/useDarkStyles';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (iso: string) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
};

const isRecordGoneError = (err: any) => {
  const status = err?.response?.status;
  if (status === 404 || status === 410) return true;

  const msg = String(
    err?.response?.data?.error ??
    err?.response?.data?.message ??
    err?.message ??
    '',
  ).toLowerCase();

  return (
    msg.includes('not found') ||
    msg.includes('no longer available') ||
    msg.includes('status code 404') ||
    msg.includes('status code 410')
  );
};

const isMissingSubmission = (sub: any) => {
  return !sub || typeof sub.id !== 'number' || Number.isNaN(sub.id);
};

interface AttachedImage {
  fileName: string;
  uri: string;
}

const normalizeAttachedImages = (imageResponse: unknown): AttachedImage[] => {
  if (!imageResponse) return [];

  const fromValue = (value: unknown, fallbackName = 'Attached image'): AttachedImage[] => {
    if (!value) return [];

    // Preferred API shape: { [originalFileName]: presignedUrl }
    if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.entries(value as Record<string, unknown>)
        .filter(([, url]) => typeof url === 'string' && url.trim().length > 0)
        .map(([name, url], idx) => ({
          fileName: (name || '').trim() || `${fallbackName} ${idx + 1}`,
          uri: (url as string).trim(),
        }));
    }

    // Backward-compatible shapes
    if (Array.isArray(value)) {
      return value
        .filter((v) => typeof v === 'string' && v.trim().length > 0)
        .map((v, idx) => ({ fileName: `${fallbackName} ${idx + 1}`, uri: (v as string).trim() }));
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      return [{ fileName: fallbackName, uri: trimmed }];
    }

    return [];
  };

  if (typeof imageResponse === 'string') {
    const raw = imageResponse.trim();
    if (!raw) return [];
    try {
      return fromValue(JSON.parse(raw));
    } catch {
      return fromValue(raw);
    }
  }

  return fromValue(imageResponse);
};

// ─── Field row ────────────────────────────────────────────────────────────────

const FieldRow: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const ms = useDarkStyles(baseMs);
  return (
    <View style={ms.fieldRow}>
      <Text style={ms.fieldLabel}>{label}</Text>
      <Text style={ms.fieldValue}>{value || '—'}</Text>
    </View>
  );
};

// ─── Main modal ───────────────────────────────────────────────────────────────

interface HaulbackRecordModalProps {
  recordId: string | null;
  onClose: () => void;
  onRecordGone: () => void; // called when record no longer exists → caller shows toast + refreshes
}

const HaulbackRecordModal: React.FC<HaulbackRecordModalProps> = ({
  recordId,
  onClose,
  onRecordGone,
}) => {
  const dc = useDarkColor();
  const ms = useDarkStyles(baseMs);
  const { t } = useTranslation('harbor');
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const haulbackRcMap = useSelector((state: RootState) => state.deliveryRpm.haulbackRcMap);
  const haulbackDtoMap = useSelector((state: RootState) => state.deliveryRpm.haulbackDtoMap);

  const [loading, setLoading] = useState(false);
  const [sub, setSub] = useState<RecentHaulbackSubmission | null>(null);
  const [notifyOpen, setNotifyOpen] = useState(false);

  // Escape key on web
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const handler = (e: any) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Load record whenever recordId changes
  useEffect(() => {
    if (!recordId) { setSub(null); return; }
    let cancelled = false;
    setLoading(true);
    setSub(null);

    fetchHaulbackSubmissionById(Number(recordId))
      .then((s) => {
        if (cancelled) return;
        if (isMissingSubmission(s)) {
          onRecordGone();
          return;
        }
        setSub(s);
      })
      .catch((err: any) => {
        if (cancelled) return;
        if (isRecordGoneError(err)) {
          onRecordGone();
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [recordId, onRecordGone]);

  const attachedImages: AttachedImage[] = normalizeAttachedImages(sub?.imageResponse);

  const rcEntry = sub ? haulbackDtoMap[sub.reasonCodeId] : undefined;
  const showPhotos = attachedImages.length > 0;
  const showNote = (rcEntry?.textFieldRequired ?? false) && !!sub?.comments;

  const getStatusLabel = (rescheduleStatus: string | null): string => {
    if (rescheduleStatus === 'Return To Stock') {
      return t('one_way.status_badges.return_to_stock');
    }
    return t('one_way.status_badges.rescheduled');
  };

  const disposition = sub ? getStatusLabel(sub.rescheduleStatus) : '';

  const visible = !!recordId;

  return (
    <>
      <Modal
        visible={visible}
        transparent
        animationType="fade"
        onRequestClose={onClose}
      >
      <View style={[ms.overlay, isMobile && ms.overlayMobile]}>
          <View style={[ms.sheet, isMobile && ms.sheetMobile]}>
            {/* ── Header ─────────────────────────────────────────────── */}
            <View style={ms.header}>
              <Text style={ms.title}>{t('haulback_record_modal.title')}</Text>
              <TouchableOpacity style={ms.closeBtn} onPress={onClose} accessibilityLabel="Close">
                <MaterialIcons name="close" size={18} color={dc("#616161")} />
              </TouchableOpacity>
            </View>

            {/* ── Body ───────────────────────────────────────────────── */}
            <ScrollView style={ms.body} contentContainerStyle={ms.bodyContent}>
              {loading && (
                <View style={ms.loadingWrap}>
                  <ActivityIndicator size="large" color={dc("#005CBC")} />
                </View>
              )}

              {!loading && sub && (
                <>
                  {/* Fields */}
                  <View style={ms.section}>
                    <View style={ms.fieldsGrid}>
                      <FieldRow label={t('haulback_record_modal.label_customer')} value={sub.customerName ?? sub.customer ?? ''} />
                      <FieldRow label={t('haulback_record_modal.label_cof')} value={''} />
                      <FieldRow label={t('haulback_record_modal.label_sscc')} value={sub.sscc ?? ''} />
                      <FieldRow label={t('haulback_record_modal.label_local_route')} value={sub.localRoute ?? ''} />
                      <FieldRow label={t('haulback_record_modal.label_date')} value={formatDate(sub.deliveryDate)} />
                      <FieldRow label={t('haulback_record_modal.label_status')} value={disposition} />
                      <FieldRow label={t('haulback_record_modal.label_reason_code')} value={haulbackRcMap[sub.reasonCodeId] ?? String(sub.reasonCodeId)} />
                      <FieldRow label={t('haulback_record_modal.label_case_quantity')} value={String(sub.caseQuantity)} />
                      <FieldRow label={t('haulback_record_modal.label_driver')} value={sub.driverName ?? sub.driver ?? ''} />
                      <FieldRow label={t('haulback_record_modal.label_sales_rep')} value={sub.salesRep ?? ''} />
                      <FieldRow label={t('haulback_record_modal.label_picker')} value={sub.picker ?? ''} />
                      {!!sub.equipmentBreakdown && (
                        <FieldRow label={t('haulback_record_modal.label_equipment_breakdown')} value={sub.equipmentBreakdown} />
                      )}
                    </View>
                  </View>

                  {/* Photos */}
                  {showPhotos && (
                    <View style={ms.section}>
                      <Text style={ms.sectionTitle}>{t('haulback_record_modal.label_images')}</Text>
                      <TruckPhotoGrid
                        photos={Object.fromEntries(attachedImages.map(img => [img.fileName, img.uri]))}
                      />
                    </View>
                  )}

                  {/* Submitted Note */}
                  {showNote && (
                    <View style={ms.section}>
                      <Text style={ms.sectionTitle}>{t('haulback_record_modal.label_details')}</Text>
                      <Text style={ms.noteText}>{sub.comments}</Text>
                    </View>
                  )}
                </>
              )}
            </ScrollView>

            {/* ── Footer ─────────────────────────────────────────────── */}
            <View style={ms.footer}>
              <TouchableOpacity
                style={ms.notifyBtn}
                onPress={() => setNotifyOpen(true)}
                accessibilityLabel="Notify my Supervisor"
              >
                <MaterialIcons name="notifications" size={16} color={dc("#034895")} style={{ marginRight: 6 }} />
                <Text style={ms.notifyBtnText}>{t('haulback_record_modal.action_notify_supervisor')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={ms.closeFooterBtn} onPress={onClose} accessibilityLabel="Close modal">
                <Text style={ms.closeFooterBtnText}>{t('haulback_record_modal.action_close')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Notify supervisor */}
      {notifyOpen && sub && (
        <NotifySupervisorModal
          recordType="Haulback"
          recordId={sub.id}
          onClose={() => setNotifyOpen(false)}
          onBack={() => setNotifyOpen(false)}
        />
      )}
    </>
  );
};

export default HaulbackRecordModal;

// ─── Modal styles ─────────────────────────────────────────────────────────────

const baseMs = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  sheet: {
    backgroundColor: '#fff',
    borderRadius: 10,
    width: '100%',
    maxWidth: 600,
    maxHeight: '90%',
    overflow: 'hidden',
    ...Platform.select({
      ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.18, shadowRadius: 24 },
      android: { elevation: 8 },
    }),
  },
  sheetMobile: { flex: 1, borderRadius: 0 },
  overlayMobile: { padding: 0 },

  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#EBEBEB',
  },
  title: { fontSize: 20, fontWeight: '700', color: '#1A1A1A', fontFamily: FontFamily.bold },
  closeBtn: {
    width: 32, height: 32, borderRadius: 6,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#F5F5F5',
  },

  body: { flex: 1 },
  bodyContent: { padding: 24, gap: 20 },

  loadingWrap: { paddingVertical: 60, alignItems: 'center' },

  section: { gap: 12 },
  sectionTitle: { fontSize: 15, fontWeight: '700', color: '#1A1A1A', fontFamily: FontFamily.bold },

  fieldsGrid: { gap: 10 },
  fieldRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  fieldLabel: {
    width: 120, fontSize: 13, fontWeight: '700', color: '#616161',
    fontFamily: FontFamily.bold, flexShrink: 0,
  },
  fieldValue: { flex: 1, fontSize: 14, color: '#1A1A1A', fontFamily: FontFamily.regular, lineHeight: 20 },

  photosGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  photoCard: { width: 100, gap: 6 },
  photoName: { fontSize: 11, color: '#616161', fontFamily: FontFamily.regular },

  noteText: {
    fontSize: 14, color: '#1A1A1A', fontFamily: FontFamily.regular,
    lineHeight: 22, backgroundColor: '#F7F8FA',
    borderRadius: 6, padding: 12,
  },

  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: '#EBEBEB',
    flexWrap: 'wrap',
    gap: 10,
  },
  notifyBtn: {
    flexDirection: 'row', alignItems: 'center',
    height: 38, paddingHorizontal: 16, borderRadius: 24,
    borderWidth: 1, borderColor: '#034895', backgroundColor: '#fff',
  },
  notifyBtnText: { fontSize: 14, fontWeight: '700', color: '#034895', fontFamily: FontFamily.bold },
  closeFooterBtn: {
    height: 38, paddingHorizontal: 28, borderRadius: 24,
    backgroundColor: '#034895', alignItems: 'center', justifyContent: 'center',
  },
  closeFooterBtnText: { fontSize: 14, fontWeight: '700', color: '#fff', fontFamily: FontFamily.bold },
});
